import Papa from 'papaparse';

/**
 * PricingEngine — Calculates glass prices from bitcreed API data.
 *
 * The volume_price_tables contain a complex CSV matrix:
 * - Row 1: Section header ("BSG monolithisch | 0 | ab 0 m²")
 * - Row 2 (FRG_STR): Short glass names, 3 columns per glass
 * - Row 3 (FRG_ID): Glass IDs matching FRG_STR positions
 * - Row 4 (MONO): Monolithic prices per FRG
 * - Following sections: ISO 2-fach and 3-fach with outside glass rows
 *
 * Each FRG occupies 3 columns: Argon price, Mischgas price, Krypton price
 */
export default class PricingEngine {
  constructor() {
    this.monoPrices = {};     // { frgId: price }
    this.isoPrices2 = {};     // { `${frgId}_${outId}_${gas}`: price }
    this.isoPrices3 = {};     // { `${frgId}_${outId}_${midId}_${gas}`: price }
    this.pricingConfig = null;
    this.ready = false;
  }

  /**
   * Initialize from API data.
   * @param {Object} pricing - pricing config (min_pos_sqm, vat_percent, etc.)
   * @param {Array} volumePriceTables - array of { qty, prices_csv }
   */
  init(pricing, volumePriceTables) {
    this.pricingConfig = pricing;

    if (!volumePriceTables || volumePriceTables.length === 0) return;

    // Parse the first (base) price table
    const basePrices = volumePriceTables[0];
    if (!basePrices?.prices_csv) return;

    this._parsePriceCSV(basePrices.prices_csv);
    this.ready = true;
  }

  _parsePriceCSV(csv) {
    const result = Papa.parse(csv, { skipEmptyLines: false });
    const rows = result.data;
    if (rows.length < 4) return;

    // Row index 1 = FRG_STR, Row index 2 = FRG_ID, Row index 3 = MONO
    // Find the FRG_ID row
    let frgIdRowIdx = -1;
    let monoRowIdx = -1;

    for (let i = 0; i < Math.min(rows.length, 10); i++) {
      const label = (rows[i][0] || '').trim();
      if (label === 'FRG_ID') frgIdRowIdx = i;
      if (label === 'MONO') monoRowIdx = i;
    }

    if (frgIdRowIdx === -1 || monoRowIdx === -1) return;

    const frgIdRow = rows[frgIdRowIdx];
    const monoRow = rows[monoRowIdx];

    // Extract FRG IDs and their column positions
    // Each FRG takes 3 columns (Argon, Mischgas, Krypton)
    const frgPositions = []; // { id, colStart }
    for (let c = 3; c < frgIdRow.length; c += 3) {
      const id = (frgIdRow[c] || '').trim();
      if (id && !isNaN(id)) {
        frgPositions.push({ id: parseInt(id), col: c });
      }
    }

    // Extract mono prices
    frgPositions.forEach(frg => {
      const price = parseFloat(monoRow[frg.col]);
      if (!isNaN(price)) {
        this.monoPrices[frg.id] = price;
      }
    });

    // Parse ISO sections (rows after MONO)
    // Look for section headers and outside glass rows
    let currentSection = null; // '2-iso' or '3-iso'
    let currentMidId = null;
    let sectionFrgPositions = [];

    for (let i = monoRowIdx + 1; i < rows.length; i++) {
      const row = rows[i];
      const label = (row[0] || '').trim();

      // Detect section headers
      if (label === '' && row.length > 3) {
        const headerCell = (row[3] || '').trim();
        if (headerCell.includes('BS-ISO 2-fach')) {
          currentSection = '2-iso';
          currentMidId = null;
        } else if (headerCell.includes('BS-ISO 3-fach')) {
          currentSection = '3-iso';
          // Extract mid glass info from header if present
          const midMatch = headerCell.match(/Mittleres Glas:\s*(.+)/);
          currentMidId = midMatch ? midMatch[1].trim() : null;
        }
        continue;
      }

      // Detect FRG_ID row within section (resets column mapping for this section)
      if (label === 'FRG_ID') {
        sectionFrgPositions = [];
        for (let c = 3; c < row.length; c += 3) {
          const id = (row[c] || '').trim();
          if (id && !isNaN(id)) {
            sectionFrgPositions.push({ id: parseInt(id), col: c });
          }
        }
        continue;
      }

      // Skip non-data rows
      if (!label || label === 'FRG_STR' || label.startsWith(',')) continue;

      // Data row: outside glass name + optional ID in column 1
      const outId = (row[1] || '').trim();
      if (!outId || isNaN(outId)) continue;

      const positions = sectionFrgPositions.length > 0 ? sectionFrgPositions : frgPositions;

      positions.forEach(frg => {
        // 3 gas columns: Argon (col), Mischgas (col+1), Krypton (col+2)
        const gases = ['Argon', 'Mischgas', 'Krypton'];
        gases.forEach((gas, gi) => {
          const price = parseFloat(row[frg.col + gi]);
          if (!isNaN(price)) {
            if (currentSection === '2-iso') {
              this.isoPrices2[`${frg.id}_${outId}_${gas}`] = price;
            } else if (currentSection === '3-iso') {
              this.isoPrices3[`${frg.id}_${outId}_${currentMidId}_${gas}`] = price;
            }
          }
        });
      });
    }
  }

  /**
   * Get the price per m² for a given glass configuration.
   * @param {number} frgId - Fire-resistant glass ID
   * @param {string} structure - 'mono', '2-glazed-ISO', '3-glazed-ISO'
   * @param {number} [outId] - Outside glass ID
   * @param {number} [midId] - Middle glass ID
   * @param {string} [gas] - Gas type: 'Argon', 'Krypton', 'Mischgas'
   * @returns {number|null} price in CHF/m² or null if not found
   */
  getPrice(frgId, structure, outId, midId, gas) {
    if (!this.ready) return null;

    if (structure === 'mono' || structure === '1-glazed (mono)') {
      return this.monoPrices[frgId] || null;
    }

    if (structure === '2-glazed-ISO') {
      const key = `${frgId}_${outId}_${gas || 'Argon'}`;
      return this.isoPrices2[key] || null;
    }

    if (structure === '3-glazed-ISO') {
      // Try with mid glass ID
      const key = `${frgId}_${outId}_${midId}_${gas || 'Argon'}`;
      return this.isoPrices3[key] || null;
    }

    return null;
  }

  /**
   * Get minimum position area in m².
   */
  getMinSqm() {
    return parseFloat(this.pricingConfig?.min_pos_sqm) || 0.5;
  }

  /**
   * Get VAT percentage.
   */
  getVatPercent() {
    return parseFloat(this.pricingConfig?.vat_percent) || 8.1;
  }

  /**
   * Get model glass factor.
   */
  getModelGlassFactor() {
    return parseFloat(this.pricingConfig?.model_glass_factor) || 1.5;
  }
}
